import { HttpRequest } from '@angular/common/http';
import { computed } from '@angular/core';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { withSessionStorage, withStorageSync } from '@ngrx-toolkit/core';

import { AppLoadingNotification, AppLoadingNotificationItem } from '@/app/app/interfaces/app/app-loading-notification';
import { VersionInfo } from '@/app/app/interfaces/app/version-info';
import { environment } from '@/app/environments/environment';
import { storagePrefix } from '@/shared/lib/constants/storage.const';
import { urlTranslationList } from '@/shared/lib/constants/translated-urls';
import { isNullOrEmpty } from '@/shared/lib/core/utils/data-validation.util';

type NotificationState = {
  notificationData: AppLoadingNotification | null
  versionInfo: VersionInfo | null;
  actionHistoryUnreadMessages: boolean;
  actionConnectorCheckUnreadMessages: boolean;
  actionMonitoringUnreadMessages: boolean;
  actionRealtimeUnreadMessages: boolean;
  signalRConnectionState: number;
};

const initialState: NotificationState = {
  notificationData: null,
  versionInfo: null,
  actionHistoryUnreadMessages: false,
  actionConnectorCheckUnreadMessages: false,
  actionMonitoringUnreadMessages: false,
  actionRealtimeUnreadMessages: false,
  signalRConnectionState: 0
};

export const NotificationStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => {
    return {
      isLoading: computed(() => (store.notificationData()?.notificationList?.length ?? 0) > 0 && store.notificationData()?.notificationList?.some((url: AppLoadingNotificationItem) => !url.isLoaded)),
    };
  }),
  withMethods((store) => ({
    setLoadingState(request: HttpRequest<any>, isLoaded: boolean) {
      let splitRequest = request.url.replace(environment.data.baseUrl, '').split('/')[1];
      if (isNullOrEmpty(splitRequest)) {
        if (request.url.includes(":")) {
          splitRequest = request.url.split(":")[2];
        }
      }
      const description = urlTranslationList.find(f => f.request.toLowerCase() === splitRequest.toLowerCase())?.description ?? `Generic: ${splitRequest}`;

      // Requests are grouped per description; count in-flight requests so the first one to finish does not mark the group as loaded
      const currentList: AppLoadingNotificationItem[] = [...(store.notificationData()?.notificationList ?? [])];
      const index = currentList.findIndex((f: AppLoadingNotificationItem) => f.description === description);
      if (isLoaded && index === -1) {
        return;
      }

      const pendingCount = Math.max((index > -1 ? currentList[index].pendingCount ?? 0 : 0) + (isLoaded ? -1 : 1), 0);

      const newNotificationItem: AppLoadingNotificationItem = {
        isLoaded: pendingCount === 0,
        pendingCount,
        description,
        showItem: true,
      };

      if (index > -1) {
        currentList[index] = newNotificationItem;
      } else {
        currentList.push(newNotificationItem);
      }

      patchState(store, (state) => {
        return {
          ...state,
          notificationData: {
            ...state.notificationData,
            notificationList: currentList.some((f: AppLoadingNotificationItem) => !f.isLoaded) ? currentList : [],
          },
        };
      });
    },
    dismissLoading() {
      // Requests still in flight are ignored when they finish, as their group is no longer in the list
      patchState(store, { notificationData: null });
    },
    setLastVersionInfo(versionInfo: VersionInfo) {
      versionInfo.lastChecked = new Date();
      patchState(store, { versionInfo });
    },
    userVersionAlreadyNotified(versionInfo: VersionInfo) {
      return store.versionInfo()?.major === versionInfo.major && store.versionInfo()?.minor === versionInfo.minor && store.versionInfo()?.patch === versionInfo.patch;
    },
    setActionUnread(data: string) {
      switch (data.toLowerCase()) {
        case 'connectorcheck':
          patchState(store, { actionConnectorCheckUnreadMessages: true });
          break;
        case 'monitoringstart':
          patchState(store, { actionMonitoringUnreadMessages: true });
          break;
        case 'monitoringstop':
          patchState(store, { actionMonitoringUnreadMessages: true });
          break;
        case 'monitoringpause':
          patchState(store, { actionMonitoringUnreadMessages: true });
          break;
        case 'monitoringresume':
          patchState(store, { actionMonitoringUnreadMessages: true });
          break;
        case 'monitoringalertreset':
          patchState(store, { actionMonitoringUnreadMessages: true });
          break;
        case 'realtime':
          patchState(store, { actionRealtimeUnreadMessages: true });
          break;
      }

      patchState(store, { actionHistoryUnreadMessages: true });
    },
    setActionRead(data: string) {
      switch (data.toLowerCase()) {
        case 'connectorcheck':
          patchState(store, { actionConnectorCheckUnreadMessages: false });
          break;
        case 'monitoring':
          patchState(store, { actionMonitoringUnreadMessages: false });
          break;
        case 'history':
          patchState(store, { actionHistoryUnreadMessages: false });
          break;
        case 'realtime':
          patchState(store, { actionRealtimeUnreadMessages: false });
          break;
      }
    },
    clear() {
      patchState(store, initialState);
    },
    setSignalRConnectionState(state: number) {
      patchState(store, { signalRConnectionState: state });
    },
  })),
  withStorageSync({
    key: `${storagePrefix}_notifications`,
    // Loading state belongs to requests of the current session only; restoring it after a reload leaves the overlay stuck
    select: (state) => ({ ...state, notificationData: null }),
  }, withSessionStorage()),
  withHooks({
    onInit(store) {
      // Clears loading state restored from storage written before it was excluded above
      patchState(store, { notificationData: null });
    },
  }),
);
